// src/app/settings/accruals/accruals.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, expand, reduce } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

import {
    LeavePolicy,
    LeavePoliciesResponse,
    LeavePolicyDetail,
    CreateLeavePolicyPayload,
    AssignEmployeesPayload,
    LeavePolicyFilterValues,

    LeaveAccrualLedger,
    LeaveAccrualLedgerResponse,
    ManualAdjustmentPayload,

    LeavePolicyStartDateRow,
    LeavePolicyStartDatesResponse,

    EmployeeUser,
    UsersResponse,
    UserFilterValues,

    EmployeeLeaveBalanceRow,
    EmployeeLeaveBalancesResponse,
} from './models/accruals.model';

@Injectable({ providedIn: 'root' })
export class AccrualsService {
    private readonly baseUrl = environment.apiBaseUrl;

    constructor(private http: HttpClient) { }

    // =====================================================
    // LEAVE POLICIES
    // =====================================================

    getLeavePolicies(): Observable<LeavePolicy[] | null> {
        return this.getAllLeavePolicies().pipe(
            map((data) => data ?? []),
            catchError((err) => {
                console.error('Error fetching leave policies:', err);
                return of(null);
            }),
        );
    }

    getAllLeavePolicies(): Observable<LeavePolicy[]> {
        return this.fetchAllPages<LeavePoliciesResponse, LeavePolicy>(
            `${this.baseUrl}/api/leave-policies`,
        );
    }

    getLeavePolicyById(id: number): Observable<LeavePolicyDetail | null> {
        return this.http
            .get<{ status: string; data: LeavePolicyDetail }>(
                `${this.baseUrl}/api/leave-policies/${id}`,
            )
            .pipe(
                map((res) => res.data),
                catchError((err) => {
                    console.error('Error fetching leave policy detail:', err);
                    return of(null);
                }),
            );
    }

    createLeavePolicy(payload: CreateLeavePolicyPayload): Observable<boolean> {
        return this.http
            .post<{ status: string }>(`${this.baseUrl}/api/leave-policies`, payload)
            .pipe(
                map(() => true),
                catchError((err) => {
                    console.error('Error creating leave policy:', err);
                    return of(false);
                }),
            );
    }

    assignEmployeesToPolicy(
        policyId: number,
        payload: AssignEmployeesPayload,
    ): Observable<boolean> {
        return this.http
            .patch<{ status: string }>(
                `${this.baseUrl}/api/leave-policies/${policyId}/assign-employees`,
                payload,
            )
            .pipe(
                map(() => true),
                catchError((err) => {
                    console.error('Error assigning employees to policy:', err);
                    return of(false);
                }),
            );
    }

    getLeavePolicyFilterValues(): Observable<LeavePolicyFilterValues | null> {
        return this.http
            .get<{ status: string; data: LeavePolicyFilterValues }>(
                `${this.baseUrl}/api/leave-policies/get-filter-values`,
            )
            .pipe(
                map((res) => res.data),
                catchError((err) => {
                    console.error('Error fetching leave policy filter values:', err);
                    return of(null);
                }),
            );
    }

    // =====================================================
    // EMPLOYEES
    // =====================================================

    getAllActiveUsers(): Observable<EmployeeUser[]> {
        return this.fetchAllPages<UsersResponse, EmployeeUser>(
            `${this.baseUrl}/api/users`,
            new HttpParams().set('employmentStatus', 'Active'),
        );
    }

    getAllUsers(): Observable<EmployeeUser[]> {
        return this.fetchAllPages<UsersResponse, EmployeeUser>(
            `${this.baseUrl}/api/users`,
        );
    }

    getUserFilterValues(): Observable<UserFilterValues | null> {
        return this.http
            .get<{ status: string; data: UserFilterValues }>(
                `${this.baseUrl}/api/users/filter-values`,
            )
            .pipe(
                map((res) => res.data),
                catchError((err) => {
                    console.error('Error fetching user filter values:', err);
                    return of(null);
                }),
            );
    }

    // =====================================================
    // EMPLOYEE BALANCES
    // =====================================================

    getEmployeeLeaveBalances(
        pageNumber: number = 1,
        pageSize: number = 100,
        employeeId?: number,
    ): Observable<EmployeeLeaveBalancesResponse | null> {
        let params = new HttpParams()
            .set('pageNumber', pageNumber)
            .set('pageSize', pageSize);

        if (employeeId) {
            params = params.set('employeeId', employeeId);
        }

        return this.http
            .get<EmployeeLeaveBalancesResponse>(
                `${this.baseUrl}/api/employee-leave-balances`,
                { params },
            )
            .pipe(
                catchError((err) => {
                    console.error('Error fetching employee leave balances:', err);
                    return of(null);
                }),
            );
    }

    getAllEmployeeLeaveBalances(): Observable<EmployeeLeaveBalanceRow[]> {
        return this.fetchAllPages<EmployeeLeaveBalancesResponse, EmployeeLeaveBalanceRow>(
            `${this.baseUrl}/api/employee-leave-balances`,
        );
    }

    // =====================================================
    // TRANSACTIONS (ACCRUAL LEDGER)
    // =====================================================

    getLeaveAccrualLedgers(
        pageNumber: number = 1,
        pageSize: number = 100,
    ): Observable<LeaveAccrualLedgerResponse | null> {
        const params = new HttpParams()
            .set('pageNumber', pageNumber)
            .set('pageSize', pageSize);

        return this.http
            .get<LeaveAccrualLedgerResponse>(
                `${this.baseUrl}/api/leave-accrual-ledgers`,
                { params },
            )
            .pipe(
                catchError((err) => {
                    console.error('Error fetching leave accrual ledgers:', err);
                    return of(null);
                }),
            );
    }

    getAllLeaveAccrualLedgers(): Observable<LeaveAccrualLedger[]> {
        return this.fetchAllPages<LeaveAccrualLedgerResponse, LeaveAccrualLedger>(
            `${this.baseUrl}/api/leave-accrual-ledgers`,
        );
    }

    createManualAdjustment(payload: ManualAdjustmentPayload): Observable<boolean> {
        return this.http
            .post<{ status: string }>(
                `${this.baseUrl}/api/leave-accrual-ledgers`,
                payload,
            )
            .pipe(
                map(() => true),
                catchError((err) => {
                    console.error('Error creating manual adjustment:', err);
                    return of(false);
                }),
            );
    }

    // =====================================================
    // POLICY HISTORY
    // =====================================================

    getLeavePolicyStartDates(
        employeeId?: number,
        leavePolicyId?: number,
    ): Observable<LeavePolicyStartDateRow[]> {
        let params = new HttpParams()
            .set('page', 1)
            .set('pageSize', 100);

        if (employeeId) {
            params = params.set('employeeId', employeeId);
        }

        if (leavePolicyId) {
            params = params.set('leavePolicyId', leavePolicyId);
        }

        return this.http
            .get<LeavePolicyStartDatesResponse>(
                `${this.baseUrl}/api/leave-policy-start-dates`,
                { params },
            )
            .pipe(
                map((res) => res.data ?? []),
                catchError((err) => {
                    console.error('Error fetching leave policy start dates:', err);
                    return of([]);
                }),
            );
    }

    getAllLeavePolicyStartDates(): Observable<LeavePolicyStartDateRow[]> {
        return this.fetchAllPages<LeavePolicyStartDatesResponse, LeavePolicyStartDateRow>(
            `${this.baseUrl}/api/leave-policy-start-dates`,
        );
    }

    deleteLeavePolicyStartDate(id: number): Observable<boolean> {
        return this.http
            .delete(`${this.baseUrl}/api/leave-policy-start-dates/${id}`, {
                observe: 'response',
                responseType: 'text',
            })
            .pipe(
                map(() => true),
                catchError((err) => {
                    console.error('Error deleting leave policy start date:', err);
                    return of(false);
                }),
            );
    }

    // =====================================================
    // HELPERS
    // =====================================================

    private fetchAllPages<
        TResponse extends {
            currentPage: number;
            totalPages: number;
            totalCount: number;
            pageSize: number;
            data: TItem[];
        },
        TItem
    >(
        url: string,
        initialParams?: HttpParams,
    ): Observable<TItem[]> {
        const defaultPageSize = 100;

        const buildParams = (page: number) => {
            let params = initialParams ?? new HttpParams();

            if (!params.has('pageNumber')) {
                params = params.set('pageNumber', page);
            } else {
                params = params.set('pageNumber', page);
            }

            if (!params.has('pageSize')) {
                params = params.set('pageSize', defaultPageSize);
            }

            return params;
        };

        const shouldFetchNextPage = (response: TResponse): boolean => {
            if (!response) {
                return false;
            }

            const currentPage = Number(response.currentPage ?? 1);
            const totalPages = Number(response.totalPages ?? 0);
            const totalCount = Number(response.totalCount ?? 0);
            const pageSize = Number(response.pageSize ?? defaultPageSize);
            const loadedCount = currentPage * pageSize;

            if (totalPages > 0) {
                return currentPage < totalPages;
            }

            if (totalCount > 0) {
                return loadedCount < totalCount;
            }

            return false;
        };

        return this.http.get<TResponse>(url, { params: buildParams(1) }).pipe(
            expand((response) => {
                if (!shouldFetchNextPage(response)) {
                    return of();
                }

                const nextPage = Number(response.currentPage ?? 1) + 1;

                return this.http.get<TResponse>(url, {
                    params: buildParams(nextPage),
                });
            }),
            map((response) => response?.data ?? []),
            reduce((all, pageData) => [...all, ...pageData], [] as TItem[]),
            catchError((err) => {
                console.error(`Error fetching paged data from ${url}:`, err);
                return of([] as TItem[]);
            }),
        );
    }
}